import {
  IonButton,
  IonContent,
  IonPage,
  IonImg,
  IonIcon,
  IonMenuToggle,
  useIonToast,
} from '@ionic/react';
import { ellipsisVertical } from 'ionicons/icons';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import {
  CasinoService,
  IPlayerDevelopmentKpis,
  IPlayerDevelopmentMix,
  IPlayerDevelopmentReportResponse,
  IPlayerDevelopmentWeekPoint,
  IRequestPlayerDevelopmentReport,
} from 'services/casino';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from 'recharts';
import { buildPdPdfFilename, buildPlayerDevelopmentPdf, downloadBlob } from './pdPdf';
import {
  downloadPlayerDevelopmentExcel,
  downloadPlayerDevelopmentExcelSummary,
  PdExcelDetailRow,
} from './pdExcel';
import { downloadPlayerDevelopmentChartPngZip } from './pdChartPng';

const AGE_BANDS = [
  'Under 18',
  'Unknown',
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65+',
] as const;
const GENDERS = ['Unknown', 'Male', 'Female'] as const;
const PIE_COLORS = ['#5B8FF9', '#F6BD16', '#F6903D', '#61DDAA', '#7262fd', '#78D3F8', '#9661BC', '#008685'];
const PAGE_SIZE = 25;
const SLOT_CHECK_CASINO_ID = 4;

export function isoWeekStartUtc(year: number, week: number): string {
  const jan4 = Date.UTC(year, 0, 4);
  const jan4Dow = new Date(jan4).getUTCDay() || 7;
  const week1Mon = jan4 - (jan4Dow - 1) * 86400000;
  const start = new Date(week1Mon + (week - 1) * 7 * 86400000);
  return start.toISOString().slice(0, 10);
}

export function weekToken(year: number, week: number): string {
  return `${year}-W${String(week).padStart(2, '0')}`;
}

export function dateToIsoWeek(d: Date): { year: number; week: number } {
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const isoYear = target.getUTCFullYear();
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Day = (jan4.getUTCDay() + 6) % 7;
  const week1Thu = new Date(jan4);
  week1Thu.setUTCDate(jan4.getUTCDate() - jan4Day + 3);
  const week = 1 + Math.round((target.getTime() - week1Thu.getTime()) / 604800000);
  return { year: isoYear, week };
}

export function formatWeekLabel(year: number, week: number): string {
  const start = isoWeekStartUtc(year, week);
  const d = new Date(`${start}T00:00:00Z`);
  const mon = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `W${String(week).padStart(2, '0')} · ${mon} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export function parseWeekToken(token: string): { year: number; week: number } | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(String(token || '').trim());
  if (!m) return null;
  const year = Number(m[1]);
  const week = Number(m[2]);
  if (!Number.isInteger(year) || !Number.isInteger(week) || week < 1 || week > 53) return null;
  return { year, week };
}

function defaultYtdWeeks(): { fromWeek: string; toWeek: string } {
  const now = dateToIsoWeek(new Date());
  const start = new Date(`${isoWeekStartUtc(now.year, now.week)}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - 7);
  const prev = dateToIsoWeek(start);
  return { fromWeek: weekToken(prev.year, prev.week), toWeek: weekToken(now.year, now.week) };
}

function weekOptionList(spanYears = 3): Array<{ token: string; label: string; weekStart: string }> {
  const now = dateToIsoWeek(new Date());
  const out: Array<{ token: string; label: string; weekStart: string }> = [];
  let y = now.year;
  let w = now.week;
  for (let i = 0; i < spanYears * 54 + 4; i += 1) {
    out.push({
      token: weekToken(y, w),
      label: formatWeekLabel(y, w),
      weekStart: isoWeekStartUtc(y, w),
    });
    const start = new Date(`${isoWeekStartUtc(y, w)}T00:00:00Z`);
    start.setUTCDate(start.getUTCDate() - 7);
    const prev = dateToIsoWeek(start);
    y = prev.year;
    w = prev.week;
  }
  return out;
}

export function niceMax(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const m = n / pow;
  const nice = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
  return nice * pow;
}

export function parseCsv(text: string): Array<Record<string, string>> {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let i = 0;
  let inQuotes = false;
  const src = String(text || '').replace(/^\uFEFF/, '');
  while (i < src.length) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      row.push(cell);
      cell = '';
      i += 1;
      continue;
    }
    if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      i += 1;
      continue;
    }
    if (ch === '\r') {
      i += 1;
      continue;
    }
    cell += ch;
    i += 1;
  }
  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows
    .slice(1)
    .filter((r) => r.some((c) => String(c || '').trim() !== ''))
    .map((r) => {
      const obj: Record<string, string> = {};
      headers.forEach((h, idx) => {
        obj[h] = r[idx] != null ? String(r[idx]) : '';
      });
      return obj;
    });
}

type MultiDropdownProps = {
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
};

const MultiDropdown = ({ label, options, selected, onChange, allLabel }: MultiDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const summary =
    selected.length === 0 || selected.length === options.length
      ? allLabel
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;
  const toggle = (value: string) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };
  return (
    <div className="player-development__field" ref={ref}>
      <span className="player-development__field-label">{label}</span>
      <div className="player-development__multi">
        <button type="button" className="player-development__multi-trigger" onClick={() => setOpen((v) => !v)}>
          {summary}
        </button>
        {open && (
          <div className="player-development__multi-panel">
            <label className="player-development__multi-option">
              <input
                type="checkbox"
                checked={selected.length === 0 || selected.length === options.length}
                onChange={() => onChange([])}
              />
              {allLabel}
            </label>
            {options.map((opt) => (
              <label key={opt} className="player-development__multi-option">
                <input
                  type="checkbox"
                  checked={selected.length === 0 ? false : selected.includes(opt)}
                  onChange={() => {
                    if (selected.length === 0) onChange([opt]);
                    else toggle(opt);
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const pdTooltipScalar = (v: unknown): string | number | undefined => {
  if (v == null || v === '') return undefined;
  if (Array.isArray(v)) return v.length ? pdTooltipScalar(v[0]) : undefined;
  if (typeof v === 'string' || typeof v === 'number') return v;
  return undefined;
};

type PdTooltipProps = {
  active?: boolean;
  payload?: Array<{
    name?: string | number;
    value?: string | number | Array<string | number>;
    color?: string;
    fill?: string;
    dataKey?: string | number;
  }>;
  label?: string | number;
  metricLabel: string;
  weekLabel?: string;
  mixTotal?: number;
};

const PdChartTooltip = ({ active, payload, label, metricLabel, weekLabel, mixTotal }: PdTooltipProps) => {
  if (!active || !payload?.length) return null;
  const rows = payload
    .map((entry) => ({ ...entry, value: pdTooltipScalar(entry?.value), name: entry?.name }))
    .filter((entry) => entry != null && entry.value != null && entry.value !== '');
  if (!rows.length) return null;
  const header = String(weekLabel ?? label ?? '');
  const fmt = (v: number | string) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return Math.round(n).toLocaleString();
  };
  const total = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
  const countDenom = typeof mixTotal === 'number' && mixTotal >= 0 ? mixTotal : total;
  const fmtCountPct = (v: number | string) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    if (!n) return '0 · 0%';
    const pct = countDenom > 0 ? (n / countDenom) * 100 : 0;
    return `${Math.round(n).toLocaleString()} · ${pct.toFixed(1)}%`;
  };
  return (
    <div className="player-development__tooltip">
      <div className="player-development__tooltip-header">
        {header ? `${header} UTC · ${metricLabel}` : metricLabel}
      </div>
      <div className="player-development__tooltip-rows">
        {rows.map((entry) => {
          const swatch = entry.color || entry.fill || '#5B8FF9';
          const key = String(entry.dataKey ?? entry.name);
          return (
            <div key={key} className="player-development__tooltip-row">
              <span className="player-development__tooltip-swatch" style={{ backgroundColor: swatch }} />
              <span className="player-development__tooltip-label">
                {entry.name != null ? String(entry.name) : key}
              </span>
              <span className="player-development__tooltip-value">
                {fmtCountPct(entry.value as number | string)}
              </span>
            </div>
          );
        })}
      </div>
      {rows.length > 1 && (
        <div className="player-development__tooltip-total">
          <span>Total</span>
          <span>{fmtCountPct(total)}</span>
        </div>
      )}
    </div>
  );
};

const pdTooltipSharedProps = {
  wrapperStyle: { outline: 'none', zIndex: 30, pointerEvents: 'none' as const },
  allowEscapeViewBox: { x: false, y: false },
  offset: 18,
  cursor: { fill: 'rgba(255,255,255,0.06)' },
  isAnimationActive: false,
};
const pdChartMargin = { top: 8, right: 12, left: 4, bottom: 28 };
const pdLabeledMargin = { top: 22, right: 12, left: 4, bottom: 28 };

const pdBarCountLabel = (v: unknown) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '';
  return Math.round(n).toLocaleString();
};

/** Product / Designer lock — frames 13 / 13b. Do not rewrite. */
const PD_CHART_INFO = {
  g1: {
    title: 'G1 Use over weeks',
    body: 'App use events (get_me_activity) stacked by age. One bar per ISO week.',
  },
  g2: {
    title: 'G2 Visits over weeks',
    body: 'Distinct floor visit days stacked by age. One bar per ISO week.',
  },
  g3: {
    title: 'G3 Active Use & Visit',
    body: 'Players with use > 0 AND visits > 0 in that same week.',
  },
  g4: {
    title: 'G4 New Users',
    body: 'Accounts whose create date falls in that ISO week at this casino.',
  },
  g5: {
    title: 'G5 Mix',
    body: 'Latest week in range, by Age, Gender, or Gender by Age.',
  },
  g7: {
    title: 'G7 Use windows',
    body: 'Snapshot sums: Last Month · Last Week · MTD · This week. WoW = this week vs last week. MoM = MTD vs last month (never this week vs last month).',
  },
  g8: {
    title: 'G8 Visits windows',
    body: 'Same four windows for visits.',
  },
  kpiPlayers: {
    title: 'Players',
    body: 'Distinct accounts in the latest week in range, after the on-screen filters.',
  },
  kpiActive: {
    title: 'Active Use & Visit',
    body: 'Accounts with use > 0 AND visits > 0 in that same latest week.',
  },
  kpiUse: {
    title: 'Use',
    body: 'Distinct accounts with Use This Week > 0. The sum is app get_me_activity events.',
  },
  kpiVisits: {
    title: 'Visits',
    body: 'Distinct accounts with Visits This Week > 0. The sum is floor distinct casino visit days.',
  },
  kpiNew: {
    title: 'New Users',
    body: 'Accounts whose create date falls in this ISO week at this casino.',
  },
} as const;

type PdChartInfoKey = keyof typeof PD_CHART_INFO;

const PD_INFO_INSET = 8;
const PD_INFO_GAP = 8;

const PdChartInfo = ({ infoKey }: { infoKey: PdChartInfoKey }) => {
  const copy = PD_CHART_INFO[infoKey];
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  const place = useCallback(() => {
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const br = btn.getBoundingClientRect();
    const pr = pop.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const popW = pr.width || 320;
    const popH = pr.height || 120;

    let left = br.right - popW;
    if (left < PD_INFO_INSET) left = br.left;
    if (left + popW > vw - PD_INFO_INSET) left = vw - PD_INFO_INSET - popW;
    left = Math.max(PD_INFO_INSET, left);

    let top = br.bottom + PD_INFO_GAP;
    if (top + popH > vh - PD_INFO_INSET) top = br.top - PD_INFO_GAP - popH;
    if (top < PD_INFO_INSET) top = PD_INFO_INSET;
    if (top + popH > vh - PD_INFO_INSET) top = Math.max(PD_INFO_INSET, vh - PD_INFO_INSET - popH);

    setPos({ top, left, ready: true });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent | TouchEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (wrapRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onReposition = () => place();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, place]);

  return (
    <div
      className={`player-development__chart-info${open ? ' is-open' : ''}`}
      ref={wrapRef}
      onMouseEnter={() => { setPos((s) => ({ ...s, ready: false })); setOpen(true); }}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={btnRef}
        type="button"
        className="player-development__chart-info-btn"
        aria-label={copy.title}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        i
      </button>
      {open
        ? createPortal(
            <div
              ref={popRef}
              className="player-development__chart-info-pop"
              role="tooltip"
              style={{ top: pos.top, left: pos.left, visibility: pos.ready ? 'visible' : 'hidden' }}
            >
              <div className="player-development__chart-info-pop-title">{copy.title}</div>
              <div className="player-development__chart-info-pop-body">{copy.body}</div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

const PdMenuKebab = () => (
  <div className="player-development__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton fill="clear" className="player-development__menu-btn" aria-label="Open menu">
        <IonIcon icon={ellipsisVertical} className="player-development__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent player-development">
    <IonContent className="player-development">
      <PdMenuKebab />
      <div className="player-development__content">
        <div className="player-development__breadcrumb-row">
          <IonMenuToggle menu="main">
            <IonButton fill="clear" className="player-development__back" aria-label="Open menu">
              <IonImg src="assets/images/back-icon.svg" />
            </IonButton>
          </IonMenuToggle>
          <div className="player-development__breadcrumb">
            Reports / <span>Player Development</span>
          </div>
        </div>
        <div className="player-development__forbidden-card">
          <h2>403 · Reporting not enabled</h2>
          <p>
            Player Development reporting requires the Cognito group{' '}
            <strong>slotcheck-enterprise-reporting</strong>. Ask ops to assign it for your account.
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

type PdMixMode = 'age' | 'gender' | 'genderByAge';

function colorForMixSlice(name: string, index: number): string {
  const bandIdx = (AGE_BANDS as readonly string[]).indexOf(name);
  if (bandIdx >= 0) return PIE_COLORS[bandIdx % PIE_COLORS.length];
  const genderIdx = (GENDERS as readonly string[]).indexOf(name);
  if (genderIdx >= 0) return PIE_COLORS[genderIdx % PIE_COLORS.length];
  const bandPart = name.split(' · ')[0];
  const bi = (AGE_BANDS as readonly string[]).indexOf(bandPart);
  if (bi >= 0) return PIE_COLORS[(bi + index) % PIE_COLORS.length];
  return PIE_COLORS[index % PIE_COLORS.length];
}

function emptyKpis(): IPlayerDevelopmentKpis {
  return {
    players: 0,
    usedThisWeek: 0,
    visitedThisWeek: 0,
    activeUseAndVisit: 0,
    newUsers: 0,
    useLastMonth: 0,
    useLastWeek: 0,
    useMonthToDate: 0,
    useThisWeek: 0,
    visitsLastMonth: 0,
    visitsLastWeek: 0,
    visitsMonthToDate: 0,
    visitsThisWeek: 0,
    useWowPct: null,
    useMomPct: null,
    visitsWowPct: null,
    visitsMomPct: null,
  };
}

function fmtDelta(pct: number | null): { text: string; cls: 'is-up' | 'is-down' | 'is-flat' } {
  if (pct == null || !Number.isFinite(pct)) return { text: '—', cls: 'is-flat' };
  if (pct === 0) return { text: '0%', cls: 'is-flat' };
  if (pct > 0) return { text: `▲ ${pct}%`, cls: 'is-up' };
  return { text: `▼ ${Math.abs(pct)}%`, cls: 'is-down' };
}

function rowWeekToken(r: Record<string, string>): string {
  const year = String(r.Year || r.year || '').trim();
  const week = String(r.Week || r.week || '').trim();
  if (year && week) return `${year}-W${String(week).padStart(2, '0')}`;
  return '';
}

export const PlayerDevelopmentReportPage = () => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [presentToast] = useIonToast();
  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const defaults = useMemo(() => defaultYtdWeeks(), []);
  const weekOpts = useMemo(() => weekOptionList(), []);
  const [fromWeek, setFromWeek] = useState(defaults.fromWeek);
  const [toWeek, setToWeek] = useState(defaults.toWeek);
  const [ageFilter, setAgeFilter] = useState<string[]>([]);
  const [genderFilter, setGenderFilter] = useState<string[]>([]);
  const [casinoFilter, setCasinoFilter] = useState('');
  const [kpis, setKpis] = useState<IPlayerDevelopmentKpis>(emptyKpis);
  const [weekSeries, setWeekSeries] = useState<IPlayerDevelopmentWeekPoint[]>([]);
  const [mix, setMix] = useState<IPlayerDevelopmentMix | null>(null);
  const [meta, setMeta] = useState<IPlayerDevelopmentReportResponse['meta'] | null>(null);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportPhase, setReportPhase] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [csvRows, setCsvRows] = useState<Array<Record<string, string>>>([]);
  const [page, setPage] = useState(0);
  const loadSeq = useRef(0);
  const presentToastRef = useRef(presentToast);
  presentToastRef.current = presentToast;
  const [mixMode, setMixMode] = useState<PdMixMode>('age');
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFlipUp, setExportFlipUp] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [casinoOptions, setCasinoOptions] = useState<ICasinoSchema[]>([]);
  const canAccess = groups.includes('slotcheck-enterprise-reporting');

  useEffect(() => {
    if (!exportOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!exportMenuRef.current?.contains(e.target as Node)) setExportOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExportOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportOpen]);

  useEffect(() => {
    if (!exportOpen) {
      setExportFlipUp(false);
      return;
    }
    const menu = exportMenuRef.current;
    if (!menu) return;
    const btn = menu.querySelector('.player-development__export') as HTMLElement | null;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const isPhone = window.matchMedia('(max-width: 767px)').matches;
    setExportFlipUp(!isPhone && spaceBelow < 5 * 56 + 36);
  }, [exportOpen]);

  const activeAgeBands = useMemo(
    () => (ageFilter.length > 0 ? ageFilter : ([...AGE_BANDS] as string[])),
    [ageFilter]
  );
  const activeGenders = useMemo(
    () => (genderFilter.length > 0 ? genderFilter : ([...GENDERS] as string[])),
    [genderFilter]
  );
  void activeGenders;

  const ageBandLegendPayload = useMemo(
    () =>
      activeAgeBands.map((b) => ({
        value: b,
        type: 'square' as const,
        id: b,
        color: PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length],
      })),
    [activeAgeBands]
  );

  const buildParams = useCallback((): IRequestPlayerDevelopmentReport => {
    const params: IRequestPlayerDevelopmentReport = {
      casinoIds: casinoFilter,
      fromWeek,
      toWeek,
      includePartialWeek: 'true',
    };
    if (ageFilter.length > 0) params.ageBands = ageFilter.join(',');
    if (genderFilter.length > 0) params.genders = genderFilter.join(',');
    return params;
  }, [fromWeek, toWeek, ageFilter, genderFilter, casinoFilter]);

  const load = useCallback(async () => {
    if (!canAccess || !casinoFilter) {
      loadSeq.current += 1;
      setLoading(false);
      setReportPhase('idle');
      setKpis(emptyKpis());
      setWeekSeries([]);
      setMix(null);
      setMeta(null);
      setCsvRows([]);
      setEmpty(false);
      return;
    }
    const seq = ++loadSeq.current;
    setLoading(true);
    setReportPhase('loading');
    setPage(0);
    try {
      const params = buildParams();
      const [res, exportBlob] = await Promise.all([
        firstValueFrom(casinoSvc.GetPlayerDevelopmentReport(params)),
        firstValueFrom(casinoSvc.GetPlayerDevelopmentReportExport(params)).catch(() => null),
      ]);
      if (seq !== loadSeq.current) return;
      const payload = res && typeof res === 'object' ? (res as IPlayerDevelopmentReportResponse) : null;
      const nextKpis = payload?.kpis || emptyKpis();
      const series = Array.isArray(payload?.weekSeries) ? payload!.weekSeries : [];
      const nextMix = payload?.mix || null;
      const nextMeta = payload?.meta || null;
      setKpis(nextKpis);
      setWeekSeries(series);
      setMix(nextMix);
      setMeta(nextMeta);
      setEmpty(Boolean(nextMeta?.empty) || (series.length === 0 && nextKpis.players === 0));
      if (exportBlob) {
        const blob = exportBlob as unknown as Blob;
        const text = typeof blob === 'string' ? blob : await (blob as Blob).text();
        setCsvRows(parseCsv(text));
      } else {
        setCsvRows([]);
      }
      setReportPhase('ready');
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      const status = err?.response?.status || err?.status;
      setKpis(emptyKpis());
      setWeekSeries([]);
      setMix(null);
      setMeta(null);
      setCsvRows([]);
      setEmpty(true);
      setReportPhase('ready');
      if (status !== 403 && status !== 304) {
        presentToastRef
          .current({
            position: 'top',
            duration: 4000,
            color: 'danger',
            message: 'Failed to load Player Development report.',
          })
          .catch(() => null);
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [buildParams, canAccess, casinoFilter, casinoSvc]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await firstValueFrom(casinoSvc.GetAllCasinos());
        const list = (res?.DataSet || []).filter((c) => {
          if (!c.isactive || c.isComingSoon) return false;
          const id = Number(c.kpCasinoPk ?? c.casinoId);
          if (id === SLOT_CHECK_CASINO_ID) return false;
          const name = `${c.shortName || ''} ${c.displayName || ''} ${c.casinoName || ''}`.toLowerCase();
          if (name.includes('slot check') || name.includes('slotcheck')) return false;
          return true;
        });
        list.sort((a, b) =>
          (a.shortName || a.displayName || a.casinoName || '').localeCompare(
            b.shortName || b.displayName || b.casinoName || ''
          )
        );
        if (!cancelled) setCasinoOptions(list);
      } catch {
        if (!cancelled) setCasinoOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, casinoSvc]);

  const chartSeries = useMemo(() => {
    return weekSeries.map((pt) => {
      const parsed = parseWeekToken(String(pt.week || ''));
      const axis = pt.weekStart || (parsed ? isoWeekStartUtc(parsed.year, parsed.week) : String(pt.week || ''));
      const point: Record<string, number | string> = {
        week: String(pt.week || ''),
        weekStart: axis,
        axisLabel: parsed ? `W${String(parsed.week).padStart(2, '0')}` : axis,
        use: Number(pt.use || 0),
        visits: Number(pt.visits || 0),
        activeUseAndVisit: Number(pt.activeUseAndVisit || 0),
        newUsers: Number(pt.newUsers || 0),
      };
      for (const band of activeAgeBands) {
        point[`use_${band}`] = Number(pt.byAge?.[band]?.use ?? (pt as Record<string, unknown>)[`use_${band}`] ?? 0);
        point[`visits_${band}`] = Number(
          pt.byAge?.[band]?.visits ?? (pt as Record<string, unknown>)[`visits_${band}`] ?? 0
        );
      }
      return point;
    });
  }, [weekSeries, activeAgeBands]);

  const g1Max = useMemo(
    () => niceMax(Math.max(...chartSeries.map((p) => Number(p.use || 0)), 0) * 1.1),
    [chartSeries]
  );
  const g2Max = useMemo(
    () => niceMax(Math.max(...chartSeries.map((p) => Number(p.visits || 0)), 0) * 1.1),
    [chartSeries]
  );
  const g3Max = useMemo(
    () => niceMax(Math.max(...chartSeries.map((p) => Number(p.activeUseAndVisit || 0)), 0) * 1.1),
    [chartSeries]
  );
  const g4Max = useMemo(
    () => niceMax(Math.max(...chartSeries.map((p) => Number(p.newUsers || 0)), 0) * 1.1),
    [chartSeries]
  );

  const mixData = useMemo(() => {
    if (!mix) return [] as Array<{ name: string; value: number }>;
    if (mixMode === 'age') return (mix.byAge || []).map((d) => ({ name: d.ageBand, value: d.count }));
    if (mixMode === 'gender') return (mix.byGender || []).map((d) => ({ name: d.gender, value: d.count }));
    return (mix.genderByAge || [])
      .map((d) => ({ name: `${d.ageBand} · ${d.gender}`, value: d.count }))
      .filter((d) => d.value > 0);
  }, [mix, mixMode]);
  const mixTotal = useMemo(() => mixData.reduce((s, d) => s + d.value, 0), [mixData]);

  const latestWeek = meta?.latestWeek || toWeek;
  const latestWeekStart = meta?.latestWeekStart || '';
  const latestWeekLabel =
    meta?.latestWeekLabel ||
    (() => {
      const p = parseWeekToken(latestWeek);
      return p ? formatWeekLabel(p.year, p.week) : latestWeek;
    })();

  const tableAll = useMemo(() => {
    const filtered = csvRows.filter((r) => {
      const token = rowWeekToken(r);
      const start = String(r.weekStart || '').slice(0, 10);
      if (latestWeek && token === latestWeek) return true;
      if (latestWeekStart && start === latestWeekStart) return true;
      return false;
    });
    return [...filtered].sort((a, b) => {
      const as = String(a.weekStart || '');
      const bs = String(b.weekStart || '');
      if (as !== bs) return as < bs ? 1 : -1;
      return Number(b['Use This Week'] || 0) - Number(a['Use This Week'] || 0);
    });
  }, [csvRows, latestWeek, latestWeekStart]);
  const pageCount = Math.max(1, Math.ceil(tableAll.length / PAGE_SIZE));
  const tablePage = tableAll.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const excelDetailRows: PdExcelDetailRow[] = useMemo(
    () =>
      csvRows.map((r) => ({
        week: rowWeekToken(r) || `${r.Year || ''}-W${String(r.Week || '').padStart(2, '0')}`,
        weekStart: String(r.weekStart || ''),
        playerId: String(r['Player ID'] || ''),
        ageBand: String(r['Age Band'] || ''),
        gender: String(r.Gender || ''),
        activeUseAndVisit: String(r['Active Use & Visit'] || ''),
        newUser: String(r['New User'] || ''),
        useThisWeek: Number(r['Use This Week'] || 0),
        visitsThisWeek: Number(r['Visits This Week'] || 0),
        adu: Number(r['Avg Daily Use (ADU) This Week'] || 0),
        adv: Number(r['Avg Daily Visits (ADV) per Week'] || 0),
        lastLogin: String(r['Last Login'] || ''),
      })),
    [csvRows]
  );

  const casinoLabelForExport = () => {
    const casinoMeta = casinoOptions.find(
      (c) => String(c.kpCasinoPk ?? c.casinoId) === String(casinoFilter)
    );
    return casinoMeta?.shortName || casinoMeta?.displayName || casinoMeta?.casinoName || String(casinoFilter);
  };

  const fromStart =
    meta?.fromWeekStart ||
    (() => {
      const p = parseWeekToken(fromWeek);
      return p ? isoWeekStartUtc(p.year, p.week) : fromWeek;
    })();
  const toStart =
    meta?.toWeekStart ||
    (() => {
      const p = parseWeekToken(toWeek);
      return p ? isoWeekStartUtc(p.year, p.week) : toWeek;
    })();

  const exportCsv = async () => {
    try {
      const blob = await firstValueFrom(casinoSvc.GetPlayerDevelopmentReportExport(buildParams()));
      const slug = casinoLabelForExport().replace(/[^\w\-]+/g, '_');
      downloadBlob(blob as unknown as Blob, `PD_${slug}_${fromStart}_${toStart}.csv`);
    } catch {
      presentToast({ position: 'top', duration: 4000, color: 'danger', message: 'CSV export failed.' }).catch(
        () => null
      );
    }
  };

  const excelPayload = () => ({
    casinoLabel: casinoLabelForExport(),
    fromWeek,
    toWeek,
    fromWeekStart: fromStart,
    toWeekStart: toStart,
    ageLabel: ageFilter.length ? ageFilter.join(', ') : 'All',
    genderLabel: genderFilter.length ? genderFilter.join(', ') : 'All',
    generatedAt: new Date(),
    kpis,
    latestWeekLabel,
    detailRows: excelDetailRows,
  });

  const exportExcel = async (mode: 'full' | 'summary' = 'full') => {
    if (!casinoFilter || reportPhase !== 'ready') {
      presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Select a casino and wait for the report before exporting Excel.',
      }).catch(() => null);
      return;
    }
    try {
      if (mode === 'summary') await downloadPlayerDevelopmentExcelSummary(excelPayload());
      else await downloadPlayerDevelopmentExcel(excelPayload());
      setExportOpen(false);
    } catch {
      presentToast({ position: 'top', duration: 4000, color: 'danger', message: 'Excel export failed.' }).catch(
        () => null
      );
    }
  };

  const mappedWeekSeries = () =>
    weekSeries.map((m) => {
      const byBand: Record<string, { use: number; visits: number; active: number; newUsers: number }> = {};
      for (const b of activeAgeBands) {
        byBand[b] = {
          use: Number(m.byAge?.[b]?.use ?? (m as Record<string, unknown>)[`use_${b}`] ?? 0),
          visits: Number(m.byAge?.[b]?.visits ?? (m as Record<string, unknown>)[`visits_${b}`] ?? 0),
          active: Number(m.byAge?.[b]?.activeUseAndVisit ?? (m as Record<string, unknown>)[`active_${b}`] ?? 0),
          newUsers: Number(m.byAge?.[b]?.newUsers ?? (m as Record<string, unknown>)[`new_${b}`] ?? 0),
        };
      }
      return {
        week: String(m.week || ''),
        weekStart: String(m.weekStart || ''),
        use: Number(m.use || 0),
        visits: Number(m.visits || 0),
        activeUseAndVisit: Number(m.activeUseAndVisit || 0),
        newUsers: Number(m.newUsers || 0),
        byBand,
      };
    });

  const exportPdf = () => {
    if (!casinoFilter || reportPhase !== 'ready') {
      presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Select a casino and wait for the report before exporting PDF.',
      }).catch(() => null);
      return;
    }
    try {
      const g5ModeLabel =
        mixMode === 'age' ? 'Age' : mixMode === 'gender' ? 'Gender' : 'Gender counts by Age Range';
      const blob = buildPlayerDevelopmentPdf({
        casinoLabel: casinoLabelForExport(),
        fromWeek,
        toWeek,
        fromWeekStart: fromStart,
        toWeekStart: toStart,
        ageLabel: ageFilter.length ? ageFilter.join(', ') : 'All',
        genderLabel: genderFilter.length ? genderFilter.join(', ') : 'All',
        generatedAt: new Date(),
        kpis,
        latestWeekLabel,
        ageBands: activeAgeBands,
        bandColors: activeAgeBands.map(
          (b) => PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]
        ),
        weekSeries: mappedWeekSeries(),
        g5Week: latestWeekLabel,
        g5Views: [
          { label: g5ModeLabel, slices: mixData.map((d, i) => ({ ...d, color: colorForMixSlice(d.name, i) })) },
        ],
      });
      downloadBlob(blob, buildPdPdfFilename(casinoLabelForExport(), fromStart, toStart));
      setExportOpen(false);
    } catch {
      presentToast({ position: 'top', duration: 4000, color: 'danger', message: 'PDF export failed.' }).catch(
        () => null
      );
    }
  };

  const exportChartPngs = async () => {
    if (!casinoFilter || reportPhase !== 'ready') {
      presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Select a casino and wait for the report before exporting chart PNGs.',
      }).catch(() => null);
      return;
    }
    try {
      const g5Key = mixMode === 'age' ? 'Age' : mixMode === 'gender' ? 'Gender' : 'GenderByAge';
      const g5Title =
        mixMode === 'age'
          ? 'G5 · Mix · Age'
          : mixMode === 'gender'
            ? 'G5 · Mix · Gender'
            : 'G5 · Mix · Gender counts by Age Range';
      await downloadPlayerDevelopmentChartPngZip({
        casinoLabel: casinoLabelForExport(),
        fromWeekStart: fromStart,
        toWeekStart: toStart,
        ageBands: activeAgeBands,
        bandColors: activeAgeBands.map(
          (b) => PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]
        ),
        weekSeries: mappedWeekSeries(),
        g5Week: latestWeekLabel,
        g5Views: [
          {
            key: g5Key,
            title: g5Title,
            mix: mixData.map((d, i) => ({ ...d, color: colorForMixSlice(d.name, i) })),
          },
        ],
        windows: {
          use: [kpis.useLastMonth, kpis.useLastWeek, kpis.useMonthToDate, kpis.useThisWeek],
          visits: [kpis.visitsLastMonth, kpis.visitsLastWeek, kpis.visitsMonthToDate, kpis.visitsThisWeek],
          useWowPct: kpis.useWowPct,
          useMomPct: kpis.useMomPct,
          visitsWowPct: kpis.visitsWowPct,
          visitsMomPct: kpis.visitsMomPct,
        },
      });
      setExportOpen(false);
    } catch {
      presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message: 'Chart PNG export failed.',
      }).catch(() => null);
    }
  };

  const resetFilters = () => {
    const next = defaultYtdWeeks();
    setFromWeek(next.fromWeek);
    setToWeek(next.toWeek);
    setAgeFilter([]);
    setGenderFilter([]);
    setCasinoFilter('');
    setReportPhase('idle');
    setKpis(emptyKpis());
    setWeekSeries([]);
    setMix(null);
    setMeta(null);
    setCsvRows([]);
    setEmpty(false);
    setLoading(false);
  };

  if (!canAccess) return <ForbiddenCard />;
  void loading;

  const useWow = fmtDelta(kpis.useWowPct);
  const useMom = fmtDelta(kpis.useMomPct);
  const visWow = fmtDelta(kpis.visitsWowPct);
  const visMom = fmtDelta(kpis.visitsMomPct);
  const g7Data = [
    { name: 'Last Month', value: kpis.useLastMonth },
    { name: 'Last Week', value: kpis.useLastWeek },
    { name: 'Month to date', value: kpis.useMonthToDate },
    { name: 'This week', value: kpis.useThisWeek },
  ];
  const g8Data = [
    { name: 'Last Month', value: kpis.visitsLastMonth },
    { name: 'Last Week', value: kpis.visitsLastWeek },
    { name: 'Month to date', value: kpis.visitsMonthToDate },
    { name: 'This week', value: kpis.visitsThisWeek },
  ];
  const g7Sum = g7Data.reduce((s, d) => s + d.value, 0);
  const g8Sum = g8Data.reduce((s, d) => s + d.value, 0);
  const g7Max = niceMax(Math.max(...g7Data.map((d) => d.value), 0) * 1.1);
  const g8Max = niceMax(Math.max(...g8Data.map((d) => d.value), 0) * 1.1);

  return (
    <IonPage className="app-parent player-development">
      <IonContent className="player-development">
        <PdMenuKebab />
        <div className="player-development__content">
          <div className="player-development__breadcrumb-row">
            <IonMenuToggle menu="main">
              <IonButton fill="clear" className="player-development__back" aria-label="Open menu">
                <IonImg src="assets/images/back-icon.svg" />
              </IonButton>
            </IonMenuToggle>
            <div className="player-development__breadcrumb">
              Reports / <span>Player Development</span>
            </div>
          </div>

          <div className="player-development__header">
            <div className="player-development__title-row">
              <h1 className="player-development__title">Player Development</h1>
            </div>
            <div className="player-development__subtitle-row">
              <p className="player-development__subtitle">
                Use, Visits, Active Use & Visit — Week × Age × Gender · one casino
              </p>
            </div>
          </div>

          <div className="player-development__filters">
            <div className="player-development__field">
              <span className="player-development__field-label">Casino</span>
              <select
                className="player-development__select"
                value={casinoFilter}
                onChange={(e) => setCasinoFilter(e.target.value)}
              >
                <option value="">Select casino</option>
                {casinoOptions.map((c) => {
                  const id = c.kpCasinoPk ?? c.casinoId;
                  const label = c.shortName || c.displayName || c.casinoName || String(id);
                  return (
                    <option key={id} value={String(id)}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="player-development__field">
              <span className="player-development__field-label">From week</span>
              <select
                className="player-development__select"
                value={fromWeek}
                onChange={(e) => {
                  const next = e.target.value;
                  setFromWeek(next);
                  if (next > toWeek) setToWeek(next);
                }}
              >
                {weekOpts.map((w) => (
                  <option key={`from-${w.token}`} value={w.token}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="player-development__field">
              <span className="player-development__field-label">To week</span>
              <select
                className="player-development__select"
                value={toWeek}
                onChange={(e) => {
                  const next = e.target.value;
                  setToWeek(next);
                  if (next < fromWeek) setFromWeek(next);
                }}
              >
                {weekOpts.map((w) => (
                  <option key={`to-${w.token}`} value={w.token}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
            <MultiDropdown label="Age" options={AGE_BANDS} selected={ageFilter} onChange={setAgeFilter} allLabel="All" />
            <MultiDropdown
              label="Gender"
              options={GENDERS}
              selected={genderFilter}
              onChange={setGenderFilter}
              allLabel="All"
            />
            <div className="player-development__export-field">
              <span className="player-development__field-label" aria-hidden="true">
                &nbsp;
              </span>
              <div className="player-development__export-menu" ref={exportMenuRef}>
                <IonButton
                  className="player-development__export"
                  disabled={!casinoFilter}
                  onClick={() => setExportOpen((o) => !o)}
                >
                  Export ▾
                </IonButton>
                {exportOpen && (
                  <>
                    <button
                      type="button"
                      className="player-development__export-backdrop"
                      aria-label="Close export menu"
                      onClick={() => setExportOpen(false)}
                    />
                    <div
                      className={`player-development__export-dropdown${exportFlipUp ? ' is-up' : ''}`}
                      role="menu"
                    >
                      <div className="player-development__export-sheet-chrome" aria-hidden="true">
                        <span className="player-development__export-sheet-handle" />
                        <div className="player-development__export-sheet-title">Export</div>
                      </div>
                      <button type="button" role="menuitem" disabled={!casinoFilter || reportPhase !== 'ready'} onClick={() => void exportExcel('summary')}>
                        <span className="player-development__export-item-title">Excel Summary</span>
                        <span className="player-development__export-item-sub">Summary sheet only</span>
                      </button>
                      <button type="button" role="menuitem" disabled={!casinoFilter || reportPhase !== 'ready'} onClick={() => void exportExcel('full')}>
                        <span className="player-development__export-item-title">Excel Summary and Detail Data</span>
                        <span className="player-development__export-item-sub">Full workbook</span>
                      </button>
                      <button type="button" role="menuitem" disabled={!casinoFilter} onClick={() => { setExportOpen(false); void exportCsv(); }}>
                        <span className="player-development__export-item-title">CSV</span>
                        <span className="player-development__export-item-sub">Raw table download</span>
                      </button>
                      <button type="button" role="menuitem" disabled={!casinoFilter || reportPhase !== 'ready'} onClick={() => { setExportOpen(false); exportPdf(); }}>
                        <span className="player-development__export-item-title">PDF snapshot</span>
                        <span className="player-development__export-item-sub">GM / ops pack</span>
                      </button>
                      <button type="button" role="menuitem" disabled={!casinoFilter || reportPhase !== 'ready'} onClick={() => void exportChartPngs()}>
                        <span className="player-development__export-item-title">Chart PNGs (zip)</span>
                        <span className="player-development__export-item-sub">G1–G8 images</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {reportPhase === 'idle' && (
            <div className="player-development__card player-development__empty player-development__empty-gate">
              <h3>Select a casino to load Player Development.</h3>
              <p className="player-development__empty-hint">
                Casino catalog is single-select. No All. Nothing fetches until a property is chosen — then the
                report auto-refetches like Player Engagement.
              </p>
            </div>
          )}

          {reportPhase === 'loading' && (
            <>
              <div className="player-development__kpi-row">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="player-development__kpi">
                    <div className="player-development__skeleton" />
                    <div className="player-development__skeleton" />
                  </div>
                ))}
              </div>
              <div className="player-development__charts">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="player-development__chart-card">
                    <div className="player-development__skeleton player-development__skeleton--lg" />
                  </div>
                ))}
              </div>
            </>
          )}

          {reportPhase === 'ready' && (
            <div className="player-development__kpi-row">
              <div className="player-development__kpi player-development__kpi-hover">
                <div className="player-development__kpi-title">
                  <div className="player-development__kpi-label">Players</div>
                  <PdChartInfo infoKey="kpiPlayers" />
                </div>
                <div className="player-development__kpi-value">{kpis.players.toLocaleString()}</div>
                <div className="player-development__kpi-meta">
                  <span className="player-development__kpi-hint">unique in latest week</span>
                </div>
              </div>
              <div className="player-development__kpi player-development__kpi-hover">
                <div className="player-development__kpi-title">
                  <div className="player-development__kpi-label">Active Use & Visit</div>
                  <PdChartInfo infoKey="kpiActive" />
                </div>
                <div className="player-development__kpi-value">{kpis.activeUseAndVisit.toLocaleString()}</div>
                <div className="player-development__kpi-meta">
                  <span className="player-development__kpi-hint">use&gt;0 and visits&gt;0</span>
                </div>
              </div>
              <div className="player-development__kpi player-development__kpi-hover">
                <div className="player-development__kpi-title">
                  <div className="player-development__kpi-label">Use</div>
                  <PdChartInfo infoKey="kpiUse" />
                </div>
                <div className="player-development__kpi-value">{kpis.usedThisWeek.toLocaleString()}</div>
                <div className="player-development__kpi-meta">
                  <span className="player-development__kpi-hint">users with use&gt;0</span>
                  <span className="player-development__kpi-vol">sum {kpis.useThisWeek.toLocaleString()}</span>
                </div>
              </div>
              <div className="player-development__kpi player-development__kpi-hover">
                <div className="player-development__kpi-title">
                  <div className="player-development__kpi-label">Visits</div>
                  <PdChartInfo infoKey="kpiVisits" />
                </div>
                <div className="player-development__kpi-value">{kpis.visitedThisWeek.toLocaleString()}</div>
                <div className="player-development__kpi-meta">
                  <span className="player-development__kpi-hint">users with visits&gt;0</span>
                  <span className="player-development__kpi-vol">sum {kpis.visitsThisWeek.toLocaleString()}</span>
                </div>
              </div>
              <div className="player-development__kpi player-development__kpi-hover">
                <div className="player-development__kpi-title">
                  <div className="player-development__kpi-label">New Users</div>
                  <PdChartInfo infoKey="kpiNew" />
                </div>
                <div className="player-development__kpi-value">{kpis.newUsers.toLocaleString()}</div>
                <div className="player-development__kpi-meta">
                  <span className="player-development__kpi-hint">first week in casino</span>
                </div>
              </div>
            </div>
          )}

          {reportPhase === 'ready' && empty && (
            <div className="player-development__card player-development__empty">
              <h3>No Player Development data for this range</h3>
              <p>Try expanding the week range, clearing age or gender filters, or picking another casino.</p>
              <IonButton className="player-development__reset" onClick={resetFilters}>
                Reset filters
              </IonButton>
            </div>
          )}

          {reportPhase === 'ready' && !empty && (
            <>
              <div className="player-development__charts">
                <div className="player-development__chart-card">
                  <div className="player-development__chart-head">
                    <div className="player-development__chart-head-text">
                      <div className="player-development__chart-title">Use over weeks</div>
                      <div className="player-development__chart-sub">G1 · stacked by age · week start</div>
                    </div>
                    <PdChartInfo infoKey="g1" />
                  </div>
                  <div className="player-development__chart-body">
                    <div className="player-development__chart">
                      <ResponsiveContainer>
                        <BarChart data={chartSeries} margin={pdChartMargin}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                          <XAxis dataKey="axisLabel" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, g1Max]} stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <Tooltip {...pdTooltipSharedProps} content={(props) => <PdChartTooltip {...props} metricLabel="Use" />} />
                          <Legend payload={ageBandLegendPayload} />
                          {activeAgeBands.map((b) => (
                            <Bar key={b} dataKey={`use_${b}`} name={b} stackId="u" fill={PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div className="player-development__chart-card">
                  <div className="player-development__chart-head">
                    <div className="player-development__chart-head-text">
                      <div className="player-development__chart-title">Visits over weeks</div>
                      <div className="player-development__chart-sub">G2 · stacked by age · week start</div>
                    </div>
                    <PdChartInfo infoKey="g2" />
                  </div>
                  <div className="player-development__chart-body">
                    <div className="player-development__chart">
                      <ResponsiveContainer>
                        <BarChart data={chartSeries} margin={pdChartMargin}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                          <XAxis dataKey="axisLabel" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, g2Max]} stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <Tooltip {...pdTooltipSharedProps} content={(props) => <PdChartTooltip {...props} metricLabel="Visits" />} />
                          <Legend payload={ageBandLegendPayload} />
                          {activeAgeBands.map((b) => (
                            <Bar key={b} dataKey={`visits_${b}`} name={b} stackId="v" fill={PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div className="player-development__chart-card">
                  <div className="player-development__chart-head">
                    <div className="player-development__chart-head-text">
                      <div className="player-development__chart-title">Active Use & Visit over weeks</div>
                      <div className="player-development__chart-sub">G3 · use&gt;0 AND visits&gt;0 in the same week</div>
                    </div>
                    <PdChartInfo infoKey="g3" />
                  </div>
                  <div className="player-development__chart-body">
                    <div className="player-development__chart">
                      <ResponsiveContainer>
                        <BarChart data={chartSeries} margin={pdLabeledMargin}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                          <XAxis dataKey="axisLabel" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, g3Max]} stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <Tooltip {...pdTooltipSharedProps} content={(props) => <PdChartTooltip {...props} metricLabel="Active Use & Visit" />} />
                          <Bar dataKey="activeUseAndVisit" name="Active Use & Visit" fill="#5B8FF9">
                            <LabelList dataKey="activeUseAndVisit" position="top" formatter={pdBarCountLabel} fill="rgba(255,255,255,0.85)" fontSize={11} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div className="player-development__chart-card">
                  <div className="player-development__chart-head">
                    <div className="player-development__chart-head-text">
                      <div className="player-development__chart-title">New Users over weeks</div>
                      <div className="player-development__chart-sub">G4 · first observed week at this casino</div>
                    </div>
                    <PdChartInfo infoKey="g4" />
                  </div>
                  <div className="player-development__chart-body">
                    <div className="player-development__chart">
                      <ResponsiveContainer>
                        <BarChart data={chartSeries} margin={pdLabeledMargin}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                          <XAxis dataKey="axisLabel" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, g4Max]} stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <Tooltip {...pdTooltipSharedProps} content={(props) => <PdChartTooltip {...props} metricLabel="New Users" />} />
                          <Bar dataKey="newUsers" name="New Users" fill="#5B8FF9">
                            <LabelList dataKey="newUsers" position="top" formatter={pdBarCountLabel} fill="rgba(255,255,255,0.85)" fontSize={11} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              <div className="player-development__bottom">
                <div className="player-development__chart-card player-development__mix-card" style={{ marginBottom: 0 }}>
                  <div className="player-development__chart-head">
                    <div className="player-development__chart-head-text">
                      <div className="player-development__chart-title">Mix for latest week</div>
                      <div className="player-development__chart-sub">G5 · {latestWeekLabel}</div>
                    </div>
                    <div className="player-development__toggle player-development__toggle--g5" role="tablist" aria-label="Mix view">
                      <IonButton className={`player-development__toggle-btn ${mixMode === 'age' ? 'is-active' : ''}`} onClick={() => setMixMode('age')}>Age</IonButton>
                      <IonButton className={`player-development__toggle-btn ${mixMode === 'gender' ? 'is-active' : ''}`} onClick={() => setMixMode('gender')}>Gender</IonButton>
                      <IonButton className={`player-development__toggle-btn player-development__toggle-btn--wide ${mixMode === 'genderByAge' ? 'is-active' : ''}`} onClick={() => setMixMode('genderByAge')}>Gender counts by Age Range</IonButton>
                    </div>
                    <PdChartInfo infoKey="g5" />
                  </div>
                  <div className="player-development__chart-body player-development__mix-body">
                    <div className="player-development__chart player-development__chart--mix">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                          <Pie data={mixData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="42%" outerRadius="68%" paddingAngle={2}>
                            {mixData.map((d, i) => (
                              <Cell key={d.name} fill={colorForMixSlice(d.name, i)} />
                            ))}
                          </Pie>
                          <Tooltip
                            {...pdTooltipSharedProps}
                            content={(props) => (
                              <PdChartTooltip
                                {...props}
                                metricLabel={mixMode === 'age' ? 'Mix · Age' : mixMode === 'gender' ? 'Mix · Gender' : 'Mix · Gender counts by Age Range'}
                                weekLabel={latestWeekLabel}
                                mixTotal={mixTotal}
                              />
                            )}
                          />
                          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="12">
                            {mixTotal.toLocaleString()} players
                          </text>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="player-development__mix-legend" aria-label="Mix legend">
                      {mixData.map((d, i) => {
                        const pct = mixTotal > 0 ? (d.value / mixTotal) * 100 : 0;
                        return (
                          <li key={d.name} className="player-development__mix-legend-item">
                            <span className="player-development__mix-legend-swatch" style={{ backgroundColor: colorForMixSlice(d.name, i) }} />
                            <span className="player-development__mix-legend-label">{d.name}</span>
                            <span className="player-development__mix-legend-meta">
                              {Math.round(d.value).toLocaleString()} · {pct.toFixed(1)}%
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="player-development__windows">
                <div className="player-development__chart-card player-development__window-card">
                  <div className="player-development__chart-head">
                    <div className="player-development__chart-head-text">
                      <div className="player-development__chart-title">Use windows</div>
                      <div className="player-development__chart-sub">G7 · Last Month · Last Week · Month to date · This week</div>
                    </div>
                    <PdChartInfo infoKey="g7" />
                  </div>
                  <div className="player-development__chart-body">
                    <div className="player-development__chart">
                      <ResponsiveContainer>
                        <BarChart data={g7Data} margin={pdLabeledMargin}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                          <XAxis dataKey="name" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, g7Max]} stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <Tooltip {...pdTooltipSharedProps} content={(props) => <PdChartTooltip {...props} metricLabel="Use windows" mixTotal={g7Sum} />} />
                          <Bar dataKey="value" name="Use" fill="#5B8FF9" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="value" position="top" formatter={pdBarCountLabel} fill="rgba(255,255,255,0.85)" fontSize={11} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="player-development__window-deltas">
                    <div className="player-development__window-tile">
                      <div className="player-development__window-tile-label">Use WoW</div>
                      <div className={`player-development__window-tile-value ${useWow.cls}`}>{useWow.text}</div>
                      <div className="player-development__window-tile-sub">This week vs last week</div>
                    </div>
                    <div className="player-development__window-tile">
                      <div className="player-development__window-tile-label">Use MoM</div>
                      <div className={`player-development__window-tile-value ${useMom.cls}`}>{useMom.text}</div>
                      <div className="player-development__window-tile-sub">MTD vs last month · not This week</div>
                    </div>
                  </div>
                </div>
                <div className="player-development__chart-card player-development__window-card">
                  <div className="player-development__chart-head">
                    <div className="player-development__chart-head-text">
                      <div className="player-development__chart-title">Visits windows</div>
                      <div className="player-development__chart-sub">G8 · Last Month · Last Week · Month to date · This week</div>
                    </div>
                    <PdChartInfo infoKey="g8" />
                  </div>
                  <div className="player-development__chart-body">
                    <div className="player-development__chart">
                      <ResponsiveContainer>
                        <BarChart data={g8Data} margin={pdLabeledMargin}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                          <XAxis dataKey="name" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, g8Max]} stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                          <Tooltip {...pdTooltipSharedProps} content={(props) => <PdChartTooltip {...props} metricLabel="Visits windows" mixTotal={g8Sum} />} />
                          <Bar dataKey="value" name="Visits" fill="#F6BD16" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="value" position="top" formatter={pdBarCountLabel} fill="rgba(255,255,255,0.85)" fontSize={11} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="player-development__window-deltas">
                    <div className="player-development__window-tile">
                      <div className="player-development__window-tile-label">Visits WoW</div>
                      <div className={`player-development__window-tile-value ${visWow.cls}`}>{visWow.text}</div>
                      <div className="player-development__window-tile-sub">This week vs last week</div>
                    </div>
                    <div className="player-development__window-tile">
                      <div className="player-development__window-tile-label">Visits MoM</div>
                      <div className={`player-development__window-tile-value ${visMom.cls}`}>{visMom.text}</div>
                      <div className="player-development__window-tile-sub">MTD vs last month · not This week</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="player-development__chart-card player-development__table-card">
                <div className="player-development__chart-head">
                  <div className="player-development__chart-head-text">
                    <div className="player-development__chart-title">Player-week roster</div>
                    <div className="player-development__chart-sub">latest week on screen · sort week start desc, Use desc</div>
                  </div>
                </div>
                <div className="player-development__table-scroll">
                  <table className="pe-table">
                    <thead>
                      <tr>
                        <th>Week start</th>
                        <th>Player ID</th>
                        <th>Age</th>
                        <th>Gender</th>
                        <th>Active Use & Visit</th>
                        <th>New User</th>
                        <th>Use This Week</th>
                        <th>Visits This Week</th>
                        <th>ADU</th>
                        <th>ADV</th>
                        <th>Last Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tablePage.map((r, idx) => {
                        const token = rowWeekToken(r);
                        const parsed = parseWeekToken(token);
                        const weekCell = parsed
                          ? `${r.weekStart || ''} (W${String(parsed.week).padStart(2, '0')})`
                          : r.weekStart || token;
                        const active = String(r['Active Use & Visit'] || '');
                        return (
                          <tr key={`${token}-${r['Player ID'] || idx}-${idx}`}>
                            <td>{weekCell}</td>
                            <td>{r['Player ID']}</td>
                            <td>{r['Age Band']}</td>
                            <td>{r.Gender}</td>
                            <td className={active.toLowerCase() === 'yes' ? 'player-development__yes' : ''}>{active || 'No'}</td>
                            <td>{r['New User'] || 'No'}</td>
                            <td className="num">{r['Use This Week']}</td>
                            <td className="num">{r['Visits This Week']}</td>
                            <td className="num">{Number(r['Avg Daily Use (ADU) This Week'] || 0).toFixed(2)}</td>
                            <td className="num">{Number(r['Avg Daily Visits (ADV) per Week'] || 0).toFixed(2)}</td>
                            <td>{r['Last Login']}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="player-development__pager">
                  <span>
                    Showing {latestWeekLabel} · {tableAll.length ? page * PAGE_SIZE + 1 : 0}–
                    {Math.min(tableAll.length, (page + 1) * PAGE_SIZE)} of {tableAll.length} player-weeks this week
                  </span>
                  <div className="player-development__pager-btns">
                    <IonButton size="small" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                      Prev
                    </IonButton>
                    <IonButton size="small" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>
                      Next
                    </IonButton>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PlayerDevelopmentReportPage;
